import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Row, Col, Card, Button, Tag, Progress, Space, Tooltip, Alert, Spin, message, Tabs, Empty } from "antd";
import {
  TruckOutlined,
  PhoneOutlined,
  MessageOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import api from "../../../shared/api.jsx";
import { logError, logInfo } from "../../../shared/utils/logger.js";
import MatchAndQuoteModal from "./MatchAndQuoteModal.jsx";
import ActiveJobCard from "./ActiveJobCard.jsx";

const ControlRoomContainer = styled.div`
  padding: 0;
`;

const HeroSection = styled(Card)`
  margin-bottom: 24px;
  background: linear-gradient(135deg, #005249 0%, #007a6b 100%);
  border: none;
  color: white;

  .ant-card-body {
    padding: 24px;
  }

  h2 {
    color: white;
    margin: 0 0 16px 0;
    font-size: 20px;
    font-weight: 600;
  }
`;

const CapacityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &:last-child {
    border-bottom: none;
  }
`;

const RouteLabel = styled.div`
  font-weight: 600;
  min-width: 200px;
`;

const FillBar = styled.div`
  flex: 1;
  margin: 0 16px;
`;

const FillPercentage = styled.div`
  font-weight: 600;
  min-width: 80px;
  text-align: right;
`;

const MainContent = styled(Row)`
  margin-bottom: 24px;
`;

const ReadyToMatchCard = styled(Card)`
  height: 100%;
  min-height: 500px;

  .ant-card-head-title {
    font-weight: 600;
    color: #005249;
  }
`;

const LooseLoadsCard = styled(Card)`
  height: 100%;
  min-height: 500px;

  .ant-card-head-title {
    font-weight: 600;
    color: #faad14;
  }
`;

const MatchRow = styled.div`
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 12px;
  background: ${(props) => (props.$highlight ? "#f6ffed" : "white")};

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RouteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const RouteName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #005249;
`;

const RevenueBadge = styled(Tag)`
  font-size: 14px;
  font-weight: 600;
  padding: 4px 12px;
`;

const LoadInfo = styled.div`
  display: flex;
  gap: 16px;
  margin: 8px 0;
  font-size: 13px;
  color: #666;
  flex-wrap: wrap;
`;

const ActionButtons = styled(Space)`
  margin-top: 12px;
`;

const LooseLoadRow = styled.div`
  padding: 12px;
  border-left: 3px solid ${(props) => props.$color || "#faad14"};
  background: ${(props) => props.$bgColor || "#fffbe6"};
  border-radius: 4px;
  margin-bottom: 8px;
`;

const ActionQueueCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-head-title {
    font-weight: 600;
    color: #ff4d4f;
  }
`;

const QueueItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff1f0;
  border-left: 3px solid #ff4d4f;
  border-radius: 4px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
`;

const HealthMetricsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
    color: #005249;
  }
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #005249;
`;

const TrendIndicator = styled.span`
  font-size: 12px;
  color: ${(props) => (props.$positive ? "#52c41a" : "#ff4d4f")};
  margin-left: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  flex-wrap: wrap;
  gap: 8px;
`;

const ShareLoadControlRoom = () => {
  const [loading, setLoading] = useState(true);
  const [capacityData, setCapacityData] = useState(null);
  const [readyToMatch, setReadyToMatch] = useState([]);
  const [looseLoads, setLooseLoads] = useState([]);
  const [actionQueue, setActionQueue] = useState([]);
  const [healthMetrics, setHealthMetrics] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [activeTab, setActiveTab] = useState("active-jobs");
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  useEffect(() => {
    fetchControlRoomData();
    const interval = setInterval(fetchControlRoomData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchControlRoomData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/dashboard/share-load-control-room");
      if (response.success) {
        setCapacityData(response.data.capacity);
        setReadyToMatch(response.data.readyToMatch || []);
        setLooseLoads(response.data.looseLoads || []);
        setActionQueue(response.data.actionQueue || []);
        setHealthMetrics(response.data.healthMetrics);
        setActiveJobs(response.data.activeJobs || []);
        logInfo("Control room data fetched successfully");
      } else {
        logError("Failed to fetch control room data", { error: response.error });
        message.error("Failed to load control room data");
      }
    } catch (error) {
      logError("Error fetching control room data", { error: error.message });
      message.error("Failed to load control room data");
    } finally {
      setLoading(false);
    }
  };

  const handleMatchAndQuote = async (matchId) => {
    const match = readyToMatch.find((m) => m.id === matchId);
    if (match) {
      setSelectedMatch(match);
      setMatchModalVisible(true);
    }
  };

  const handleWhatsApp = (phone) => {
    if (!phone) {
      message.warning("Phone number not available");
      return;
    }
    const whatsappUrl = `https://wa.me/${phone.replace(/[^0-9]/g, "")}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCall = (phone) => {
    if (!phone) {
      message.warning("Phone number not available");
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  const getFillColor = (percentage) => {
    if (percentage >= 80) return "#52c41a";
    if (percentage >= 60) return "#faad14";
    return "#ff4d4f";
  };

  const getRiskLevel = (percentage) => {
    if (percentage < 50) return "HIGH";
    if (percentage < 70) return "MEDIUM";
    return "LOW";
  };

  const getJobsByStatus = (status) => {
    return activeJobs.filter((job) => job.status === status);
  };

  const inProgressJobs = getJobsByStatus("in_progress");
  const confirmedJobs = getJobsByStatus("confirmed");
  const scheduledJobs = getJobsByStatus("scheduled");

  if (loading && !capacityData) {
    return (
      <ControlRoomContainer>
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>
              Loading control room data...
            </div>
          </div>
        </Card>
      </ControlRoomContainer>
    );
  }

  return (
    <ControlRoomContainer>
      {/* Hero Section: Today's Capacity */}
      <HeroSection>
        <h2>Today's Capacity</h2>
        {capacityData?.routes?.map((route, idx) => (
          <CapacityRow key={idx}>
            <RouteLabel>{route.name}</RouteLabel>
            <FillBar>
              <Progress
                percent={route.fillPercentage}
                strokeColor={getFillColor(route.fillPercentage)}
                showInfo={false}
              />
            </FillBar>
            <FillPercentage>{route.fillPercentage}% filled</FillPercentage>
          </CapacityRow>
        ))}
        <SummaryRow>
          <span>
            Trucks active today: <strong>{capacityData?.activeTrucks || 0}</strong>
          </span>
          <span>
            Empty space risk:{" "}
            <strong>{getRiskLevel(capacityData?.averageFill || 0)}</strong>
          </span>
        </SummaryRow>
      </HeroSection>

      {/* Main Content: Tabs for Active Jobs and Share Load Matching */}
      <Card style={{ marginBottom: 24 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={
              <span>
                <PlayCircleOutlined /> Active Jobs ({activeJobs.length})
              </span>
            }
            key="active-jobs"
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Tag color="success">
                  In Progress: {inProgressJobs.length}
                </Tag>
                <Tag color="processing">
                  Confirmed: {confirmedJobs.length}
                </Tag>
                <Tag color="warning">
                  Scheduled: {scheduledJobs.length}
                </Tag>
                <Button
                  icon={<ReloadOutlined />}
                  size="small"
                  onClick={fetchControlRoomData}
                  loading={loading}
                  style={{ marginLeft: "auto" }}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {loading && activeJobs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spin size="large" />
              </div>
            ) : activeJobs.length === 0 ? (
              <Empty description="No active jobs found" />
            ) : (
              <Row gutter={[16, 16]}>
                {activeJobs.map((job) => (
                  <Col xs={24} sm={12} lg={8} xl={6} key={job.id}>
                    <ActiveJobCard job={job} />
                  </Col>
                ))}
              </Row>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <ThunderboltOutlined /> Shared load Matching
              </span>
            }
            key="share-load"
          >
            {/* Main Content: Ready-to-Match & Loose Loads */}
            <MainContent gutter={[24, 24]}>
              <Col xs={24} lg={14}>
                <ReadyToMatchCard
                  title="Ready-to-Match Loads (High Revenue Potential)"
                  extra={
                    <Button
                      icon={<ReloadOutlined />}
                      size="small"
                      onClick={fetchControlRoomData}
                      loading={loading}
                    >
                      Refresh
                    </Button>
                  }
                >
            {readyToMatch.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                No ready-to-match loads found
              </div>
            ) : (
              readyToMatch.map((match, idx) => (
                <MatchRow key={idx} $highlight={match.priority === "high"}>
                  <RouteHeader>
                    <RouteName>{match.route}</RouteName>
                    <RevenueBadge color="green">
                      R{match.potentialRevenue?.toLocaleString()}
                    </RevenueBadge>
                  </RouteHeader>
                  <LoadInfo>
                    <span>
                      <strong>{match.loadCount}</strong> partial loads
                    </span>
                    <span>•</span>
                    <span>
                      Combined fill: <strong>{match.combinedFill}%</strong>
                    </span>
                    <span>•</span>
                    <span>
                      Window: <strong>{match.dateWindow}</strong>
                    </span>
                  </LoadInfo>
                  <div style={{ marginTop: "8px" }}>
                    <Progress
                      percent={match.combinedFill}
                      strokeColor={getFillColor(match.combinedFill)}
                      size="small"
                    />
                  </div>
                  <ActionButtons>
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleMatchAndQuote(match.id)}
                    >
                      Match & Send Quotes
                    </Button>
                    {match.contacts?.map((contact, cIdx) => (
                      <React.Fragment key={cIdx}>
                        <Button
                          icon={<PhoneOutlined />}
                          onClick={() => handleCall(contact.phone)}
                        >
                          Call
                        </Button>
                        <Button
                          icon={<MessageOutlined />}
                          onClick={() => handleWhatsApp(contact.phone)}
                        >
                          WhatsApp
                        </Button>
                      </React.Fragment>
                    ))}
                  </ActionButtons>
                </MatchRow>
              ))
            )}
          </ReadyToMatchCard>
        </Col>

        <Col xs={24} lg={10}>
          <LooseLoadsCard
            title="Loose Loads (Needs Pairing)"
            extra={<Tag color="orange">{looseLoads.length} waiting</Tag>}
          >
            {looseLoads.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                No loose loads found
              </div>
            ) : (
              looseLoads.map((load, idx) => (
                <LooseLoadRow
                  key={idx}
                  $color={load.age > 3 ? "#ff4d4f" : "#faad14"}
                  $bgColor={load.age > 3 ? "#fff1f0" : "#fffbe6"}
                >
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                    {load.route}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                    {load.loadSize} • {load.flexibility}
                  </div>
                  <div style={{ fontSize: "11px", color: "#999", marginBottom: "8px" }}>
                    Waiting: {load.age} {load.age === 1 ? "day" : "days"}
                  </div>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      message.info("Find compatible load feature coming soon");
                    }}
                  >
                    Find compatible load →
                  </Button>
                </LooseLoadRow>
              ))
            )}
          </LooseLoadsCard>
        </Col>
      </MainContent>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Action Queue */}
      {actionQueue.length > 0 && (
        <ActionQueueCard title="Action Queue (Do This Next)">
          {actionQueue.map((item, idx) => (
            <QueueItem key={idx}>
              <div style={{ flex: 1 }}>
                <Space>
                  <WarningOutlined style={{ color: "#ff4d4f" }} />
                  <span style={{ fontWeight: 600 }}>{item.message}</span>
                  <Tag>{item.route}</Tag>
                  <span style={{ color: "#666" }}>{item.details}</span>
                </Space>
              </div>
              <Space>
                {item.actions?.map((action, aIdx) => (
                  <Button
                    key={aIdx}
                    size="small"
                    icon={
                      action.type === "whatsapp" ? (
                        <MessageOutlined />
                      ) : (
                        <PhoneOutlined />
                      )
                    }
                    onClick={() => {
                      if (action.type === "whatsapp") handleWhatsApp(action.phone);
                      if (action.type === "call") handleCall(action.phone);
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Space>
            </QueueItem>
          ))}
        </ActionQueueCard>
      )}

      {/* Share Load Health Metrics */}
      {healthMetrics && (
        <HealthMetricsCard title="Shared load Health (7-Day View)">
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={6}>
              <MetricRow>
                <MetricLabel>Avg Truck Fill</MetricLabel>
                <div>
                  <MetricValue>{healthMetrics.avgTruckFill}%</MetricValue>
                  <TrendIndicator $positive={healthMetrics.avgTruckFillTrend > 0}>
                    <ArrowUpOutlined />
                    {Math.abs(healthMetrics.avgTruckFillTrend)}%
                  </TrendIndicator>
                </div>
              </MetricRow>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricRow>
                <MetricLabel>Loads Matched Today</MetricLabel>
                <div>
                  <MetricValue>{healthMetrics.loadsMatchedToday}</MetricValue>
                  <TrendIndicator $positive={healthMetrics.loadsMatchedTrend > 0}>
                    <ArrowUpOutlined />
                    {Math.abs(healthMetrics.loadsMatchedTrend)}
                  </TrendIndicator>
                </div>
              </MetricRow>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricRow>
                <MetricLabel>Dead Km Avoided</MetricLabel>
                <MetricValue>{healthMetrics.deadKmAvoided} km</MetricValue>
              </MetricRow>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricRow>
                <MetricLabel>Avg Time to Match</MetricLabel>
                <MetricValue>{healthMetrics.avgTimeToMatch}</MetricValue>
              </MetricRow>
            </Col>
          </Row>
        </HealthMetricsCard>
      )}

      {/* Match & Quote Modal */}
      <MatchAndQuoteModal
        visible={matchModalVisible}
        matchData={selectedMatch}
        onClose={() => {
          setMatchModalVisible(false);
          setSelectedMatch(null);
        }}
        onSuccess={() => {
          fetchControlRoomData();
          message.success("Quotes sent successfully!");
        }}
      />
    </ControlRoomContainer>
  );
};

export default ShareLoadControlRoom;

